/**
 * Taxi-plan persistence — CRUD over the `taxi_plans` table. The whole node/edge
 * /group network lives in the `graph` JSONB column; we map the DB row ↔ the
 * typed `TaxiPlan` here so callers work with the domain shape only.
 */
import { supabase } from '@/lib/supabase'
import type {
  ActiveWindow,
  TaxiGraphData,
  TaxiPlan,
} from '@/demo/masters-planner/data/taxiGraph'

interface TaxiPlanRow {
  id: string
  organization_id: string
  location_id: string
  name: string
  description: string | null
  is_baseline: boolean
  active: unknown
  graph: unknown
  flags: unknown
  deleted_at: string | null
}

const DEFAULT_GRAPH: TaxiGraphData = { groups: [], nodes: [], edges: [], runways: [] }

function rowToPlan(row: TaxiPlanRow): TaxiPlan {
  const graph = (row.graph as TaxiGraphData | null) ?? DEFAULT_GRAPH
  return {
    id: row.id,
    locationId: row.location_id,
    name: row.name,
    description: row.description ?? undefined,
    isBaseline: row.is_baseline,
    active: (row.active as ActiveWindow | null) ?? { mode: 'always', enabled: true },
    groups: graph.groups ?? [],
    nodes: graph.nodes ?? [],
    edges: graph.edges ?? [],
    runways: graph.runways ?? [],
    flags: (row.flags as Record<string, unknown> | null) ?? {},
  }
}

/** Columns written on insert/update — the graph collapses to one JSONB blob. */
function planToWrite(plan: TaxiPlan): Record<string, unknown> {
  return {
    name: plan.name,
    description: plan.description ?? null,
    is_baseline: plan.isBaseline,
    active: plan.active,
    graph: {
      groups: plan.groups,
      nodes: plan.nodes,
      edges: plan.edges,
      runways: plan.runways ?? [],
    } satisfies TaxiGraphData,
    flags: plan.flags ?? {},
  }
}

export async function listTaxiPlans(locationId: string): Promise<TaxiPlan[]> {
  const { data, error } = await supabase
    .from('taxi_plans')
    .select('*')
    .eq('location_id', locationId)
    .is('deleted_at', null)
    .order('is_baseline', { ascending: false })
    .order('created_at')
  if (error) throw error
  return ((data as unknown as TaxiPlanRow[]) ?? []).map(rowToPlan)
}

export async function getTaxiPlan(id: string): Promise<TaxiPlan | null> {
  const { data, error } = await supabase.from('taxi_plans').select('*').eq('id', id).single()
  if (error) throw error
  return data ? rowToPlan(data as unknown as TaxiPlanRow) : null
}

/** Insert a new plan/version; returns the persisted plan (with the DB id). */
export async function createTaxiPlan(
  organizationId: string,
  plan: TaxiPlan
): Promise<TaxiPlan> {
  const { data, error } = await supabase
    .from('taxi_plans')
    .insert({
      organization_id: organizationId,
      location_id: plan.locationId,
      ...planToWrite(plan),
    })
    .select()
    .single()
  if (error) throw error
  return rowToPlan(data as unknown as TaxiPlanRow)
}

/** Update an existing plan by DB id. */
export async function updateTaxiPlan(id: string, plan: TaxiPlan): Promise<TaxiPlan> {
  const { data, error } = await supabase
    .from('taxi_plans')
    .update(planToWrite(plan))
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return rowToPlan(data as unknown as TaxiPlanRow)
}

/** Duplicate a plan's graph under a new name (never baseline). */
export async function duplicateTaxiPlan(
  organizationId: string,
  source: TaxiPlan,
  name: string
): Promise<TaxiPlan> {
  return createTaxiPlan(organizationId, {
    ...source,
    name,
    isBaseline: false,
    flags: { ...source.flags, duplicatedFrom: source.id },
  })
}

/** Soft-delete a plan. */
export async function deleteTaxiPlan(id: string): Promise<void> {
  const { error } = await supabase
    .from('taxi_plans')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}
